import { useEffect, useState, type ReactNode } from 'react';
import {
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  getDashboardComponentData,
  getDashboardComponentOptions,
  type DashboardSource,
} from '../../../../services/dashboardService';
import PanelError from './PanelError';
import PanelRefreshButton from './PanelRefreshButton';
import PanelOptionsDropdown from './PanelOptionsDropdown';

// The dashboard service types `IChartData.values` as `Map<string, number>`, but
// the backend returns a plain JSON object at runtime. Normalize to a record so
// we can iterate it (mirrors the existing Portal panels' `Object.*` usage).
const toRecord = (values: unknown): Record<string, number> =>
  (values ?? {}) as Record<string, number>;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalM,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
    color: tokens.colorBrandForeground1,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  bars: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    overflowY: 'auto',
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '40% 1fr auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  barLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barTrack: {
    height: '10px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground4,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground,
  },
  barValue: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '24px',
    textAlign: 'right',
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

interface BarChartPanelProps {
  title: string;
  code: string;
  source: DashboardSource;
  icon?: ReactNode;
  // Bumped by the parent's global Refresh to re-run fetches in place (no
  // remount), avoiding an aborted request wave.
  refreshKey?: number;
}

const BarChartPanel = ({
  title,
  code,
  source,
  icon,
  refreshKey = 0,
}: BarChartPanelProps) => {
  const styles = useStyles();
  const [options, setOptions] = useState<{ key: string; text: string }[]>();
  const [groupBy, setGroupBy] = useState('');
  const [data, setData] = useState<[string, number][] | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by Retry to re-run the data fetch after a failure.
  const [reloadKey, setReloadKey] = useState(0);

  // Group-by choices come from the options endpoint on the chart's own source.
  // The first option is selected by default; these charts always send a real
  // value. A failed options fetch surfaces as an error (and stops loading),
  // same as data.
  useEffect(() => {
    const ac = new AbortController();
    setOptions(undefined);
    setFailed(false);
    getDashboardComponentOptions(ac, code, source)
      .then((res) => {
        const opts = Object.values(toRecord(res.values)).map((value) => ({
          key: String(value),
          text: String(value),
        }));
        setOptions(opts);
        setGroupBy((prev) => prev || opts[0]?.key || '');
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        // A failed options fetch is non-fatal: fall back to no options so the
        // data call still fires (with no `options` param). For a component that
        // truly has options the data call may then 500 and surface the error,
        // same as before but one step later; optionless components work fine.
        setOptions([]);
      });
    return () => ac.abort();
  }, [code, source, reloadKey, refreshKey]);

  const hasOptions = (options?.length ?? 0) > 0;

  useEffect(() => {
    // Wait until options have resolved. A component with options requires a
    // chosen value (firing with an empty one 500s); a component with no options
    // fetches its data immediately with no `options` param.
    // NB: this effect intentionally does NOT depend on reloadKey/refreshKey.
    // A refresh re-runs the options effect, which flips `options` (undefined ->
    // list); the data fetch follows that change exactly once. Adding the keys
    // here would fire a stale-options fetch that gets immediately aborted
    // (the "canceled then success" double wave).
    if (options === undefined) return;
    if (hasOptions && groupBy === '') return;
    const ac = new AbortController();
    setData(null);
    setFailed(false);
    const optionValue = hasOptions ? groupBy : undefined;
    getDashboardComponentData(ac, source, code, optionValue)
      .then((res) => setData(Object.entries(toRecord(res.values))))
      .catch(() => {
        if (ac.signal.aborted) return;
        setFailed(true);
      });
    return () => ac.abort();
  }, [code, source, groupBy, options, hasOptions]);

  const max = data ? Math.max(1, ...data.map(([, v]) => v)) : 1;
  // Loading while options are resolving or the data fetch is in flight; never
  // "loading" once we've failed (so the in-panel refresh re-enables).
  const loading = !failed && (options === undefined || data === null);

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <div className={styles.titleGroup}>
          {icon && <span className={styles.icon}>{icon}</span>}
          <Text className={styles.title}>{title}</Text>
        </div>
        <div className={styles.headerActions}>
          {hasOptions && (
            <PanelOptionsDropdown
              options={options}
              selected={groupBy}
              onSelect={setGroupBy}
            />
          )}
          <PanelRefreshButton
            loading={loading}
            onRefresh={() => setReloadKey((k) => k + 1)}
          />
        </div>
      </div>
      {failed ? (
        <PanelError onRetry={() => setReloadKey((k) => k + 1)} />
      ) : data === null ? (
        <Skeleton>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Array.from({ length: 4 }, (_, i) => (
              <SkeletonItem key={i} size={16} />
            ))}
          </div>
        </Skeleton>
      ) : data.length === 0 ? (
        <div className={styles.empty}>
          <Text size={200}>No data</Text>
        </div>
      ) : (
        <div className={styles.bars}>
          {data.map(([name, value]) => (
            <div key={name} className={styles.barRow}>
              <Text className={styles.barLabel} title={name}>
                {name}
              </Text>
              <div className={styles.barTrack}>
                <div
                  className={styles.barFill}
                  style={{ width: `${(value / max) * 100}%` }}
                />
              </div>
              <Text className={styles.barValue}>{value}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarChartPanel;
